import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  DestroyRef,
  ElementRef,
  inject,
  input,
  InputSignal,
  OnDestroy,
  OnInit,
  output,
  OutputEmitterRef,
  ViewChild,
  viewChild,
} from '@angular/core';
import {
  Scene,
  WebGLRenderer,
  Group,
  Material,
  PMREMGenerator,
  Object3D,
  Mesh,
  Vector3,
  Euler,
  AnimationClip,
  OrthographicCamera,
  PerspectiveCamera,
} from 'three';
import { ThreeLoader } from '../class/loader';
import { interval, Subject, throttle } from 'rxjs';
import { CommonModule } from '@angular/common';
import * as THREE from 'three';

// SRP에 맞게 분리된 클래스들 import
import { Render } from '../class/render';
import { ObjectControl } from '../class/object-control';
import { EnvironmentControl } from '../class/envirenment-control';
import { CameraManager } from '../class/camera';
import { AnimationManager } from '../class/animation-manager';
import { StickerManager } from '../class/sticker-manager';
import { RaycastManager } from '../class/raycast-manager';
import { EventManager } from '../class/event-manager';
import { CanvasControllerService } from '../../services/canvas-controller/canvas-controller.service';
import { ProjectControllerService } from '../../layout/layouts/workspace-layout/commons/services/project-controller/project-controller.service';
import { LayoutService } from '../../layout/layout.service';

@Component({
  selector: 'scattre-canvas',
  imports: [CommonModule],
  templateUrl: './canvas.component.html',
  styleUrl: './canvas.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ThreeCanvasComponent implements OnInit, OnDestroy, AfterViewInit {
  private readonly destroyRef = inject(DestroyRef);

  // Scene 참조 저장
  private _scene: Scene | undefined;
  private _resizeObserver: ResizeObserver | null = null;
  private _canvasSize = { width: 0, height: 0 };

  @ViewChild('canvasDom') public canvasDom: ElementRef<HTMLDivElement> | undefined;

  public readonly mouseEventFrame: InputSignal<number> = input(30);

  public readonly created: OutputEmitterRef<ThreeCanvasComponent | undefined> = output<
    ThreeCanvasComponent | undefined
  >();
  public readonly changeMeshes: OutputEmitterRef<Map<string, Mesh>> = output<Map<string, Mesh>>();
  public readonly changeMaterials: OutputEmitterRef<Map<string, Material>> =
    output<Map<string, Material>>();
  public readonly cutSticker: OutputEmitterRef<string> = output<string>();
  public readonly isLoading: OutputEmitterRef<boolean> = output<boolean>();
  public readonly deleteStickerAttachMeshData: OutputEmitterRef<{ index: number; name: string }> =
    output<{ index: number; name: string }>();

  // SRP에 맞게 분리된 매니저들
  private renderManager: Render | null = null;
  private objectManager: ObjectControl | null = null;
  private environmentManager: EnvironmentControl | null = null;
  private cameraManager: CameraManager | null = null;
  private animationManager: AnimationManager | null = null;
  private stickerManager: StickerManager | null = null;
  private raycastManager: RaycastManager | null = null;
  private eventManager: EventManager | null = null;

  // 프로젝트 구독 관련
  private projectSubscription: any = null;
  private currentProject: any = null;

  public SceneGraph = null;
  public instance: any = undefined;
  public ngtCanvas = viewChild.required('canvas');

  public isRaycast: boolean | undefined = false;
  public isDestroy: boolean = false;
  public isStickerControl: boolean = true;
  public isAutoRotation: boolean = true;

  public deleteStickerName: string = '';
  public deleteStickerIndex: number = -1;
  public state: any = undefined;

  public depth: number = 5; // depth 속성 업데이트

  public baseObject: Object3D | undefined;

  // Mesh 및 Material 분류 데이터 저장
  private meshCategories: Map<string, THREE.Mesh[]> = new Map();
  private activeMaterials: Map<string, THREE.Material> = new Map();
  private inactiveMaterials: Map<string, THREE.Material> = new Map();

  public decalPosition: Vector3 | undefined;
  public decalEuler: Euler | undefined;
  public decalSize: Vector3 | undefined;
  public decalDepthBox: Mesh | undefined;
  public orthCamera: OrthographicCamera | undefined;

  public pmremGenerator: PMREMGenerator | undefined;

  private mouseMove$: Subject<any> = new Subject<any>(); // 마우스 이벤트 스트림
  private loader: ThreeLoader = new ThreeLoader(); // 로더 인스턴스 추가

  constructor(
    public layoutService: LayoutService,
    private canvasService: CanvasControllerService,
    private cd: ChangeDetectorRef,
  ) {
    this.mouseMove$
      .pipe(throttle(() => interval(this.mouseEventFrame()))) // 마우스 이벤트를 지정된 간격으로 제한
      .subscribe((event) => this.handleMouseMove());
  }

  ngOnInit(): void {
    this.isDestroy = false;
  }

  ngAfterViewInit(): void {
    this.initializeManagers();
    this.initializeThreeJS();

    // Canvas Service에 컴포넌트 전송
    this.sendCanvasToService();
  }

  ngOnDestroy(): void {
    try {
      // ResizeObserver 정리
      if (this._resizeObserver) {
        this._resizeObserver.disconnect();
        this._resizeObserver = null;
      }

      this.disposeAllManagers();
      this.disposeThreeJS();

      // 프로젝트 구독 해제
      this.unsubscribeFromProject();

      this.isDestroy = true;
    } catch (error) {
      console.error('컴포넌트 정리 중 오류 발생:', error);
    }
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Private methods - 매니저 초기화 및 정리
  // -----------------------------------------------------------------------------------------------------

  private initializeManagers(): void {
    try {
      // 각 매니저 인스턴스 생성 (한 번만)
      this.eventManager = new EventManager();
      this.raycastManager = new RaycastManager();
      this.objectManager = new ObjectControl();
      this.environmentManager = new EnvironmentControl();
      this.cameraManager = new CameraManager();
      this.animationManager = new AnimationManager();
      this.stickerManager = new StickerManager();
      // Render 매니저는 나중에 초기화 (DOM과 카메라가 준비된 후)
    } catch (error) {
      console.error('매니저 초기화 중 오류 발생:', error);
    }
  }

  private initializeThreeJS(): void {
    try {
      if (!this.canvasDom?.nativeElement) {
        return;
      }

      // CameraManager를 통해 카메라 생성
      const initialCamera = this.cameraManager?.getActiveCamera();
      if (!initialCamera) return;
      initialCamera.position.set(8, 5, 8);
      initialCamera.lookAt(0, 1, 0);

      // Canvas 요소 직접 생성
      const canvas = document.createElement('canvas');
      canvas.width = this.canvasDom.nativeElement.clientWidth || 800;
      canvas.height = this.canvasDom.nativeElement.clientHeight || 600;
      canvas.style.width = '100%';
      canvas.style.height = '100%';

      // Canvas를 DOM에 추가
      this.canvasDom.nativeElement.appendChild(canvas);

      // Scene 생성
      const scene = new THREE.Scene();
      scene.name = 'MainScene';
      this._scene = scene;

      // Render 매니저 설정 (기존 구조 유지)
      this.renderManager = new Render(
        this.canvasDom.nativeElement,
        canvas,
        this._scene,
        initialCamera,
        60,
        Date.now(),
      );

      // Render 매니저에 CameraManager 설정
      if (this.renderManager && this.cameraManager) {
        this.renderManager.setCameraManager(this.cameraManager);
      }

      // CameraManager에 카메라 추가

      // 기본 상태 객체 생성
      const state = {
        scene: this._scene,
        gl: this.renderManager?.getRenderer(),
        camera: initialCamera,
      };

      // 매니저들에 상태 설정
      this.initializeManagersWithState(state);

      // 렌더링 루프 시작 (Render 클래스 활용)
      this.startRenderLoop();

      // 현실적인 기본 조명 시스템 추가
      this.setupRealisticLighting();

      // CameraManager에 자동 회전 설정 위임
      this.setupCameraAutoRotation();

      // CameraManager에 CameraControls 초기화 요청 (async)
      this.initializeCameraControls().catch((error) => {
        console.error('CameraControls 초기화 실패:', error);
      });

      // ResizeObserver 초기화
      this.initializeResizeObserver();
    } catch (error) {
      console.error('Three.js 초기화 중 오류 발생:', error);
    }
  }

  /**
   * 현실적인 기본 조명 시스템 설정
   */
  private setupRealisticLighting(): void {
    try {
      if (!this._scene) return;

      // 1. 주변광 (Ambient Light) - 전체적인 기본 밝기
      const ambientLight = new THREE.AmbientLight(0xffffff, 0.3);
      ambientLight.name = 'AmbientLight';
      this._scene.add(ambientLight);

      // 2. 주요 방향성 조명 (Directional Light) - 태양광 역할, 그림자 생성
      const mainLight = new THREE.DirectionalLight(0xffffff, 1.0);
      mainLight.position.set(50, 100, 50);
      mainLight.castShadow = true;
      mainLight.shadow.mapSize.width = 2048;
      mainLight.shadow.mapSize.height = 2048;
      mainLight.shadow.camera.near = 0.5;
      mainLight.shadow.camera.far = 500;
      mainLight.shadow.camera.left = -100;
      mainLight.shadow.camera.right = 100;
      mainLight.shadow.camera.top = 100;
      mainLight.shadow.camera.bottom = -100;
      mainLight.shadow.bias = -0.0001;
      mainLight.name = 'MainDirectionalLight';
      this._scene.add(mainLight);

      // 3. 보조 조명 (Fill Light) - 그림자 부분을 부드럽게 채움
      const fillLight = new THREE.DirectionalLight(0xffffff, 0.4);
      fillLight.position.set(-30, 50, -30);
      fillLight.castShadow = false;
      fillLight.name = 'FillLight';
      this._scene.add(fillLight);

      // 4. 리무즈 조명 (Rim Light) - 오브젝트 윤곽선 강조
      const rimLight = new THREE.DirectionalLight(0xffffff, 0.3);
      rimLight.position.set(0, 0, 50);
      rimLight.castShadow = false;
      rimLight.name = 'RimLight';
      this._scene.add(rimLight);

      // 5. 하단 보조 조명 (Bottom Fill) - 하단 그림자 완화
      const bottomLight = new THREE.DirectionalLight(0xffffff, 0.2);
      bottomLight.position.set(0, -50, 0);
      bottomLight.castShadow = false;
      bottomLight.name = 'BottomFillLight';
      this._scene.add(bottomLight);

      // 렌더러에 그림자 설정
      if (this.renderManager) {
        const renderer = this.renderManager.getRenderer();
        if (renderer) {
          renderer.shadowMap.enabled = true;
          renderer.shadowMap.type = THREE.PCFSoftShadowMap;
          renderer.shadowMap.autoUpdate = true;
        }
      }

      // HDR 환경 맵 로드 및 설정
      this.loadHDREnvironment();
    } catch (error) {
      console.error('조명 시스템 설정 중 오류 발생:', error);
    }
  }

  /**
   * HDR 환경 맵 로드 및 설정
   */
  private async loadHDREnvironment(): Promise<void> {
    try {
      if (!this._scene || !this.renderManager) return;

      const renderer = this.renderManager.getRenderer();
      if (!renderer) return;

      // PMREMGenerator 생성
      const pmremGenerator = new THREE.PMREMGenerator(renderer);
      pmremGenerator.compileEquirectangularShader();

      // HDR 파일 로드 (RGBELoader 사용)
      const { RGBELoader } = await import('three/examples/jsm/loaders/RGBELoader.js');
      const rgbeLoader = new RGBELoader();

      rgbeLoader.load(
        './assets/images/hdr/potsdamer_platz_1k.hdr',
        (texture) => {
          if (!this._scene) return;

          // 텍스처를 환경 맵으로 변환
          const envMap = pmremGenerator.fromEquirectangular(texture).texture;

          // Scene에 환경 맵 설정
          this._scene.environment = envMap;

          // 톤 매핑 설정으로 HDR 효과 최적화
          renderer.toneMapping = THREE.ACESFilmicToneMapping;
          renderer.toneMappingExposure = 0.78;
          renderer.outputColorSpace = THREE.SRGBColorSpace;

          // 리소스 정리
          texture.dispose();
          pmremGenerator.dispose();
        },
        undefined,
        (error) => {
          console.error('HDR 환경 맵 로드 중 오류 발생:', error);
          // HDR 로드 실패 시 기본 환경 설정
          this.setupFallbackEnvironment();
        },
      );
    } catch (error) {
      console.error('HDR 환경 맵 로드 중 오류 발생:', error);
      // HDR 로드 실패 시 기본 환경 설정
      this.setupFallbackEnvironment();
    }
  }

  /**
   * HDR 로드 실패 시 기본 환경 설정
   */
  private setupFallbackEnvironment(): void {
    try {
      if (!this._scene) return;

      // 기본 그라데이션 배경 설정
      const canvas = document.createElement('canvas');
      canvas.width = 2;
      canvas.height = 2;

      const context = canvas.getContext('2d');
      if (context) {
        const gradient = context.createLinearGradient(0, 0, 0, 2);
        gradient.addColorStop(0, '#87CEEB'); // 하늘색
        gradient.addColorStop(1, '#E0F6FF'); // 연한 하늘색

        context.fillStyle = gradient;
        context.fillRect(0, 0, 2, 2);

        const texture = new THREE.CanvasTexture(canvas);
      }
    } catch (error) {
      console.error('기본 환경 설정 중 오류 발생:', error);
    }
  }

  /**
   * ResizeObserver 초기화 및 크기 변화 감지 설정
   */
  private initializeResizeObserver(): void {
    try {
      if (!this.canvasDom?.nativeElement) return;

      // 기존 ResizeObserver 정리
      if (this._resizeObserver) {
        this._resizeObserver.disconnect();
      }

      // 새로운 ResizeObserver 생성
      this._resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const { width, height } = entry.contentRect;

          // 크기가 실제로 변경되었는지 확인
          if (width !== this._canvasSize.width || height !== this._canvasSize.height) {
            this._canvasSize = { width, height };
            this.handleCanvasResize(width, height);
          }
        }
      });

      // 캔버스 엘리먼트 관찰 시작
      this._resizeObserver.observe(this.canvasDom.nativeElement);

      // 초기 크기 설정
      const rect = this.canvasDom.nativeElement.getBoundingClientRect();
      this._canvasSize = { width: rect.width, height: rect.height };
    } catch (error) {
      console.error('ResizeObserver 초기화 중 오류 발생:', error);
    }
  }

  /**
   * 캔버스 크기 변화 처리
   */
  private handleCanvasResize(width: number, height: number): void {
    try {
      if (!this.renderManager) return;

      // 1. 렌더러 크기 조정
      const renderer = this.renderManager.getRenderer();
      if (renderer) {
        renderer.setSize(width, height, false);
        renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2)); // 성능 최적화
      }

      // 2. 카메라 aspect ratio 업데이트
      if (this.cameraManager) {
        this.cameraManager.updateCameraAspect(width / height);
      }

      // 3. 카메라 제한 사항 재계산 (필요한 경우)
      if (this.cameraManager && this._scene) {
        // 씬의 모든 오브젝트를 고려하여 카메라 제한 조정
        // this.updateCameraConstraintsForScene();
      }

      // 4. 강제 렌더링
      if (this.renderManager) {
        this.renderManager.render();
      }
    } catch (error) {
      console.error('캔버스 크기 변경 처리 중 오류 발생:', error);
    }
  }

  /**
   * 씬의 모든 오브젝트를 고려하여 카메라 제한 업데이트
   */
  private updateCameraConstraintsForScene(): void {
    try {
      if (!this._scene || !this.cameraManager) return;

      // 씬의 모든 오브젝트를 포함하는 바운딩 박스 계산
      const allObjects: THREE.Object3D[] = [];
      this._scene.traverse((object) => {
        if (object.type === 'Mesh' || object.type === 'Group') {
          allObjects.push(object);
        }
      });

      if (allObjects.length === 0) return;

      // 전체 씬을 포함하는 바운딩 박스 계산
      const sceneBox = new THREE.Box3();
      allObjects.forEach((object) => {
        const box = new THREE.Box3().setFromObject(object);
        sceneBox.expandByObject(object);
      });

      // 카메라 제한 조정
      this.cameraManager.adjustCameraConstraintsForObject(allObjects[0]); // 첫 번째 오브젝트로 대표
    } catch (error) {
      console.error('씬 기반 카메라 제한 업데이트 중 오류 발생:', error);
    }
  }

  /**
   * 조명 강도 조절 메서드
   */
  public adjustLightIntensity(lightName: string, intensity: number): void {
    try {
      if (!this._scene) return;

      const light = this._scene.getObjectByName(lightName);
      if (light && light instanceof THREE.Light) {
        light.intensity = Math.max(0, Math.min(2, intensity)); // 0~2 범위로 제한
      }
    } catch (error) {
      console.error('조명 강도 조절 중 오류 발생:', error);
    }
  }

  /**
   * 그림자 품질 향상 설정
   */
  public enhanceShadowQuality(): void {
    try {
      if (!this.renderManager) return;

      const renderer = this.renderManager.getRenderer();
      if (renderer) {
        // 그림자 맵 해상도 증가
        (renderer.shadowMap as any).mapSize.width = 4096;
        (renderer.shadowMap as any).mapSize.height = 4096;

        // 그림자 타입을 더 부드럽게
        renderer.shadowMap.type = THREE.PCFSoftShadowMap;

        // 그림자 자동 업데이트 활성화
        renderer.shadowMap.autoUpdate = true;
        renderer.shadowMap.needsUpdate = true;
      }
    } catch (error) {
      console.error('그림자 품질 향상 중 오류 발생:', error);
    }
  }

  /**
   * 조명 색상 온도 조절 (따뜻한 톤 ↔ 차가운 톤)
   */
  public adjustLightColorTemperature(lightName: string, temperature: number): void {
    try {
      if (!this._scene) return;

      const light = this._scene.getObjectByName(lightName);
      if (light && light instanceof THREE.DirectionalLight) {
        const temp = Math.max(2000, Math.min(10000, temperature));
        let color: number;

        if (temp < 4000) {
          const t = (temp - 2000) / 2000;
          color = new THREE.Color().setHSL(0.1 + t * 0.05, 0.8, 0.5).getHex();
        } else if (temp < 7000) {
          color = 0xffffff;
        } else {
          const t = (temp - 7000) / 3000;
          color = new THREE.Color().setHSL(0.6, 0.8, 0.5 - t * 0.2).getHex();
        }

        light.color.setHex(color);
      }
    } catch (error) {
      console.error('조명 색상 온도 조절 중 오류 발생:', error);
    }
  }

  private startRenderLoop(): void {
    try {
      if (!this.renderManager) {
        console.error('Render 매니저가 초기화되지 않았습니다.');
        return;
      }

      let lastTime = 0;
      const targetFrameTime = 1000 / 60; // 60fps 목표

      const animate = (currentTime: number) => {
        if (this.isDestroy) return;

        // 프레임 시간 제한으로 성능 최적화
        const deltaTime = currentTime - lastTime;
        if (deltaTime < targetFrameTime) {
          requestAnimationFrame(animate);
          return;
        }

        // Render 클래스의 render() 메서드 호출 (모든 Three.js 객체 관리)
        if (this.renderManager) {
          this.renderManager.render();
        }

        lastTime = currentTime;
        requestAnimationFrame(animate);
      };

      requestAnimationFrame(animate);
    } catch (error) {
      console.error('렌더링 루프 시작 중 오류 발생:', error);
    }
  }

  /**
   * CameraManager에 자동 회전 설정 위임
   */
  private setupCameraAutoRotation(): void {
    try {
      if (!this.cameraManager) return;

      // CameraManager에 자동 회전 설정 요청
      this.cameraManager.setupAutoRotation();
    } catch (error) {
      console.error('카메라 자동 회전 설정 위임 중 오류 발생:', error);
    }
  }

  /**
   * CameraManager에 CameraControls 초기화 요청
   */
  private async initializeCameraControls(): Promise<void> {
    try {
      if (!this.cameraManager || !this.canvasDom?.nativeElement) return;

      const canvas = this.renderManager?.getRenderer()?.domElement;
      if (!canvas) return;

      // CameraManager에 CameraControls 초기화 요청 (async)
      await this.cameraManager.initializeCameraControls(canvas);
    } catch (error) {
      console.error('CameraControls 초기화 위임 중 오류 발생:', error);
    }
  }

  private disposeThreeJS(): void {
    try {
      if (this.canvasDom?.nativeElement) {
        // 캔버스 요소 정리
        const canvas = this.canvasDom.nativeElement.querySelector('canvas');
        if (canvas) {
          this.canvasDom.nativeElement.removeChild(canvas);
        }
      }
    } catch (error) {
      console.error('Three.js 정리 중 오류 발생:', error);
    }
  }

  private initializeManagersWithState(state: any): void {
    try {
      if (this.objectManager) this.objectManager.setScene(state.scene);
      if (this.environmentManager) this.environmentManager.setScene(state.scene);
      if (this.cameraManager) this.cameraManager.setSceneAndRenderer(state.scene, state.gl);

      // CameraManager 초기화 완료 후 카메라 설정
      if (this.cameraManager) {
        this.cameraManager.addCamera(state.camera, 'perspective', 'MainCamera');
        this.cameraManager.setActiveCamera('MainCamera');
      }

      // 애니메이션 매니저 설정
      if (this.animations && this.animationManager) {
        this.animationManager.setAnimations(this.animations);
      }

      // 스티커 매니저 초기화
      if (this.stickerManager) this.stickerManager.createStickerViewer('MainStickerViewer');

      // 레이캐스트 매니저 활성화
      if (this.raycastManager) this.raycastManager.enable();

      // 이벤트 매니저 설정
      if (this.eventManager) this.eventManager.setMouseEventFrame(this.mouseEventFrame());
    } catch (error) {
      console.error('매니저 초기화 중 오류 발생:', error);
    }
  }

  private disposeAllManagers(): void {
    try {
      // 각 매니저의 리소스 정리
      if (this.renderManager) {
        this.renderManager.dispose();
        this.renderManager = null;
      }

      if (this.objectManager) {
        this.objectManager.dispose();
        this.objectManager = null;
      }

      if (this.environmentManager) {
        this.environmentManager.dispose();
        this.environmentManager = null;
      }

      if (this.cameraManager) {
        this.cameraManager.dispose();
        this.cameraManager = null;
      }

      if (this.animationManager) {
        this.animationManager.dispose();
        this.animationManager = null;
      }

      if (this.stickerManager) {
        this.stickerManager.dispose();
        this.stickerManager = null;
      }

      if (this.raycastManager) {
        this.raycastManager.dispose();
        this.raycastManager = null;
      }

      if (this.eventManager) {
        this.eventManager.dispose();
        this.eventManager = null;
      }
    } catch (error) {
      console.error('매니저 정리 중 오류 발생:', error);
    }
  }

  private handleMouseMove(): void {
    try {
      // 기본 마우스 이벤트 처리
      if (this.eventManager) {
        this.eventManager.onMouseMove();
      }
    } catch (error) {
      console.error('마우스 이벤트 처리 중 오류 발생:', error);
    }
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods - SRP 매니저 기반 구현
  // -----------------------------------------------------------------------------------------------------

  public getState(state: any): void {
    try {
      this.state = state;
      this.initializeManagersWithState(state);
      this.created.emit(this);
    } catch (error) {
      console.error('상태 설정 중 오류 발생:', error);
    }
  }

  public setCameraLocked(isLocked: boolean): void {
    try {
      if (this.cameraManager) {
        this.cameraManager.setCameraLocked(isLocked);
      }
    } catch (error) {
      console.error('카메라 잠금 설정 중 오류 발생:', error);
    }
  }

  public onRaycast(): void {
    try {
      if (this.raycastManager) {
        this.raycastManager.enable();
      }
    } catch (error) {
      console.error('레이캐스트 활성화 중 오류 발생:', error);
    }
  }

  public offRaycast(): void {
    try {
      if (this.raycastManager) {
        this.raycastManager.disable();
      }
    } catch (error) {
      console.error('레이캐스트 비활성화 중 오류 발생:', error);
    }
  }

  public rendered(ev: any): void {
    try {
      this.instance = ev.instance;
      if (!this.isAutoRotation && this.instance) {
        this.instance.autoRotate = false;
      }

      // 첫 렌더링 완료 시 Canvas Service에 컴포넌트 전송
      if (!this.canvasService.canvas) {
        this.sendCanvasToService();
      }
    } catch (error) {
      console.error('렌더링 이벤트 처리 중 오류 발생:', error);
    }
  }

  public initObject(): void {
    try {
      if (!this.scene || !this.baseObject) return;

      // 오브젝트 매니저를 통해 오브젝트 초기화
      if (this.objectManager) {
        this.objectManager.clearAllObjects();
        this.objectManager.addObject(this.baseObject, 'BaseObject');
      }

      // 애니메이션 매니저를 통해 애니메이션 설정
      if (this.animationManager) {
        this.animationManager.setAnimations(this.animations || []);
      }
    } catch (error) {
      console.error('오브젝트 초기화 중 오류 발생:', error);
    }
  }

  public autoRatation(isRotation: boolean): void {
    try {
      this.isAutoRotation = isRotation;
      if (!this.instance) return;
      this.instance.autoRotate = isRotation;
    } catch (error) {
      console.error('자동 회전 설정 중 오류 발생:', error);
    }
  }

  public clearEnvironment(): void {
    try {
      if (!this.instance) return;
      this.instance.isEnvironment = false;
      this.instance.cd.markForCheck();

      if (this.environmentManager) {
        this.environmentManager.clearEnvironment();
      }
    } catch (error) {
      console.error('환경 정리 중 오류 발생:', error);
    }
  }

  public viewEnvironment(): void {
    try {
      if (!this.instance) return;
      this.instance.isEnvironment = true;
      this.instance.cd.markForCheck();
    } catch (error) {
      console.error('환경 표시 중 오류 발생:', error);
    }
  }

  public holdCameraControls(): void {
    try {
      if (this.cameraManager) {
        this.cameraManager.holdCameraControls();
      }
    } catch (error) {
      console.error('카메라 컨트롤 잠금 중 오류 발생:', error);
    }
  }

  public releaseCameraControls(): void {
    try {
      if (this.cameraManager) {
        this.cameraManager.releaseCameraControls();
      }
    } catch (error) {
      console.error('카메라 컨트롤 해제 중 오류 발생:', error);
    }
  }

  public lookAtCenter(isAnimation: boolean): Promise<void[] | false> {
    try {
      if (!this.cameraManager) return new Promise<void[] | false>((resolve) => resolve(false));

      return this.cameraManager.fitToBox(this.objectManager?.getMainGroup() || null, isAnimation);
    } catch (error) {
      console.error('중앙 보기 중 오류 발생:', error);
      return Promise.resolve(false);
    }
  }

  public lookAtTarget(
    x: number,
    y: number,
    z: number,
    isAnimation: boolean,
  ): Promise<void | false> {
    try {
      if (!this.cameraManager) return new Promise<void | false>((resolve) => resolve(false));
      return this.cameraManager.setTarget(x, y, z, isAnimation);
    } catch (error) {
      console.error('타겟 보기 중 오류 발생:', error);
      return Promise.resolve(false);
    }
  }

  public fitShere(): Promise<any> {
    try {
      if (!this.cameraManager) return new Promise<any>((resolve) => resolve(false));
      return this.cameraManager.fitToSphere(this.objectManager?.getMainGroup() || null, true);
    } catch (error) {
      console.error('구 맞춤 중 오류 발생:', error);
      return Promise.resolve(false);
    }
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Private methods - 애니메이션 스팟 생성
  // -----------------------------------------------------------------------------------------------------

  private createAnimationSpots(scene: Group): void {
    try {
      if (!this.animationManager) return;

      scene.traverse((child: any) => {
        if (child.name.includes('Empty_') || child.name.includes('empty_')) {
          const animationCategory =
            child.name.split('Empty_')[1] || child.name.split('empty_')[1] || 'none';

          const indices = this.animations
            .filter((animation) => animation.name.includes(`(${animationCategory})`))
            .map((animation) => this.animations.indexOf(animation));

          // 애니메이션 스팟 생성
          this.animationManager!.createAnimationSpot(
            child,
            indices,
            `AnimationSpot_${animationCategory}`,
          );
        }
      });
    } catch (error) {
      console.error('애니메이션 스팟 생성 중 오류 발생:', error);
    }
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Getter methods - 매니저와의 연결
  // -----------------------------------------------------------------------------------------------------

  public get scene(): Scene | undefined {
    try {
      // Render 매니저의 Scene 반환
      return this.renderManager?.scene || this._scene || undefined;
    } catch (error) {
      console.error('Scene 가져오기 중 오류 발생:', error);
      return undefined;
    }
  }

  public get gl(): WebGLRenderer | undefined {
    try {
      return this.renderManager?.getRenderer() || undefined;
    } catch (error) {
      console.error('Renderer 가져오기 중 오류 발생:', error);
      return undefined;
    }
  }

  public get raycaster(): any {
    try {
      return this.raycastManager?.getRaycaster() || undefined;
    } catch (error) {
      console.error('Raycaster 가져오기 중 오류 발생:', error);
      return undefined;
    }
  }

  public get controls(): any {
    try {
      return this.cameraManager?.getCameraControls() || undefined;
    } catch (error) {
      console.error('CameraControls 가져오기 중 오류 발생:', error);
      return undefined;
    }
  }

  public get camera(): any {
    try {
      return this.cameraManager?.getActiveCamera() || undefined;
    } catch (error) {
      console.error('Camera 가져오기 중 오류 발생:', error);
      return undefined;
    }
  }

  public get mainGroup(): Group | undefined {
    try {
      return this.objectManager?.getMainGroup() || undefined;
    } catch (error) {
      console.error('MainGroup 가져오기 중 오류 발생:', error);
      return undefined;
    }
  }

  public get stickerViewer(): Group | undefined {
    try {
      return this.stickerManager?.getMainStickerViewer() || undefined;
    } catch (error) {
      console.error('StickerViewer 가져오기 중 오류 발생:', error);
      return undefined;
    }
  }

  public get animationsSpots(): Array<Object3D> {
    try {
      if (!this.animationManager) return [];

      const spots = this.animationManager.getAllAnimationSpots();
      return Array.from(spots.values()).map((spot) => spot.spot);
    } catch (error) {
      console.error('AnimationSpots 가져오기 중 오류 발생:', error);
      return [];
    }
  }

  public set animations(value: Array<AnimationClip>) {
    try {
      if (this.animationManager) {
        this.animationManager.setAnimations(value);
      }
    } catch (error) {
      console.error('Animations 설정 중 오류 발생:', error);
    }
  }

  public disposeAll(): void {
    try {
      this.disposeAllManagers();
    } catch (error) {
      console.error('전체 정리 중 오류 발생:', error);
    }
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Additional methods - 누락된 메서드들
  // -----------------------------------------------------------------------------------------------------

  public hideSpots(): void {
    try {
      if (this.animationManager) {
        this.animationManager.hideAnimationSpots();
      }
    } catch (error) {
      console.error('스팟 숨김 중 오류 발생:', error);
    }
  }

  public showSpots(): void {
    try {
      if (this.animationManager) {
        this.animationManager.showAnimationSpots();
      }
    } catch (error) {
      console.error('스팟 표시 중 오류 발생:', error);
    }
  }

  public reversePlayAllAnimation(): void {
    try {
      if (this.animationManager) {
        this.animationManager.reversePlayAllAnimations();
      }
    } catch (error) {
      console.error('모든 애니메이션 역재생 중 오류 발생:', error);
    }
  }

  public lookAtCenterShpere(isAnimation: boolean): Promise<any> | undefined {
    try {
      if (!this.cameraManager || !this.objectManager) return undefined;

      const mainGroup = this.objectManager.getMainGroup();
      if (!mainGroup) return undefined;

      const boundingBox = new THREE.Box3().setFromObject(mainGroup);
      const center = boundingBox.getCenter(new Vector3());

      return this.lookAtTarget(center.x, center.y, center.z, isAnimation);
    } catch (error) {
      console.error('구 중앙 보기 중 오류 발생:', error);
      return undefined;
    }
  }

  public completeAttachSticker(): void {
    try {
      // 스티커 부착 완료 로직
      if (this.stickerManager) {
        // 부착 완료 처리
      }
    } catch (error) {
      console.error('스티커 부착 완료 중 오류 발생:', error);
    }
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Additional methods - 누락된 메서드들 (계속)
  // -----------------------------------------------------------------------------------------------------

  public attachOneSideDecal(
    scale: number,
    scaleRatio: number,
    depth: number,
    rotation: number,
    stickerUrl: string,
    renderOrder: number,
    name?: string,
  ): any {
    try {
      // 단면 데칼 부착 로직
      return this.oneSideAttachSticker(renderOrder, name);
    } catch (error) {
      console.error('단면 데칼 부착 중 오류 발생:', error);
      return null;
    }
  }

  public attachDualSideDecal(
    scale: number,
    scaleRatio: number,
    depth: number,
    rotation: number,
    stickerUrl: string,
    renderOrder: number,
    name?: string,
  ): any {
    try {
      // 양면 데칼 부착 로직
      return this.dualSidedStickerAttach(renderOrder, name);
    } catch (error) {
      console.error('양면 데칼 부착 중 오류 발생:', error);
      return null;
    }
  }

  public lookAtDirection(view: 'top' | 'left' | 'right' | 'front' | 'back', isAnimation: boolean) {
    try {
      if (!this.cameraManager) return;
      this.cameraManager.lookAtDirection(view, isAnimation);
    } catch (error) {
      console.error('방향 보기 중 오류 발생:', error);
    }
  }

  public lookAtPosition(
    x: number,
    y: number,
    z: number,
    isAnimation: boolean,
  ): Promise<void | false> {
    try {
      if (!this.cameraManager) return new Promise<void | false>((resolve) => resolve(false));
      const moveY: number = Math.max(1.55, Math.min(Math.PI / 2.1, y));
      return this.cameraManager.setPosition(x, moveY, z, isAnimation);
    } catch (error) {
      console.error('위치 보기 중 오류 발생:', error);
      return Promise.resolve(false);
    }
  }

  public changeFloorColor(color: string): void {
    try {
      if (color[0] != '#') color = '#' + color;

      if (this.instance) {
        this.instance.floorColorChange(color);
      }
    } catch (error) {
      console.error('바닥 색상 변경 중 오류 발생:', error);
    }
  }

  public changeBackgroundColor(color: string): void {
    try {
      if (color[0] != '#') color = '#' + color;

      if (this.instance) {
        this.instance.bacgkroundColorChange(color);
      }
    } catch (error) {
      console.error('배경 색상 변경 중 오류 발생:', error);
    }
  }

  public changeToneMappingExposure(value: number): void {
    try {
      if (!this.renderManager) return;
      this.renderManager.setToneMappingExposure(value);
    } catch (error) {
      console.error('톤 매핑 노출 변경 중 오류 발생:', error);
    }
  }

  public playAnimation(indexs: number[]): void {
    try {
      if (!this.animationManager) return;

      // 애니메이션 스팟 ID를 찾아서 재생
      const spots = this.animationManager.getAllAnimationSpots();
      for (const [id, spotInfo] of spots) {
        if (spotInfo.animationIndices.some((index) => indexs.includes(index))) {
          this.animationManager.playAnimation(id);
        }
      }
    } catch (error) {
      console.error('애니메이션 재생 중 오류 발생:', error);
    }
  }

  public reversePlayAnimation(indexs: number[]): void {
    try {
      if (!this.animationManager) return;

      // 애니메이션 스팟 ID를 찾아서 역재생
      const spots = this.animationManager.getAllAnimationSpots();
      for (const [id, spotInfo] of spots) {
        if (spotInfo.animationIndices.some((index) => indexs.includes(index))) {
          this.animationManager.reversePlayAnimation(id);
        }
      }
    } catch (error) {
      console.error('애니메이션 역재생 중 오류 발생:', error);
    }
  }

  public materialsRoughnessChange(material: any, roughness: number): void {
    try {
      if (!material || !this.objectManager) return;
      this.objectManager.setMaterialProperty(material.id, 'roughness', roughness);
    } catch (error) {
      console.error('머티리얼 거칠기 변경 중 오류 발생:', error);
    }
  }

  public materialsMetalnessChange(material: any, metalness: number): void {
    try {
      if (!material || !this.objectManager) return;
      this.objectManager.setMaterialProperty(material.id, 'metalness', metalness);
    } catch (error) {
      console.error('머티리얼 금속성 변경 중 오류 발생:', error);
    }
  }

  public materialsMapChange(material: any, url?: string): void {
    try {
      if (!material || !this.objectManager) return;

      if (url) {
        this.objectManager.setMaterialTexture(material.id, url, 'map');
      } else {
        this.objectManager.resetMaterial(material.id);
      }
    } catch (error) {
      console.error('머티리얼 맵 변경 중 오류 발생:', error);
    }
  }

  public async addStickerViewerUrl(url: string, renderOrder: number, size?: number): Promise<Mesh> {
    try {
      if (!this.stickerManager) throw new Error('StickerManager not initialized');

      const viewerId = this.stickerManager.createStickerViewer();
      const mesh = await this.stickerManager.addStickerImage(viewerId, url, renderOrder, size);

      if (!mesh) throw new Error('Failed to create sticker');

      return mesh;
    } catch (error) {
      console.error('스티커 뷰어 추가 중 오류 발생:', error);
      throw error;
    }
  }

  public removeStickerViewer(): void {
    try {
      if (!this.stickerManager) return;

      // 모든 스티커 뷰어 제거
      const viewers = this.stickerManager.getAllStickerViewers();
      viewers.forEach((viewerInfo) => {
        this.stickerManager!.removeStickerViewer(viewerInfo.id);
      });
    } catch (error) {
      console.error('스티커 뷰어 제거 중 오류 발생:', error);
    }
  }

  public stickerViewerScale(scale: number): void {
    try {
      if (!this.stickerManager) return;

      // 메인 스티커 뷰어의 스케일 변경
      const mainViewer = this.stickerManager.getMainStickerViewer();
      if (mainViewer) {
        mainViewer.scale.set(scale / 3, scale / 3, mainViewer.scale.z);
      }
    } catch (error) {
      console.error('스티커 뷰어 스케일 변경 중 오류 발생:', error);
    }
  }

  public stickerViewerRotation(rotation: number): void {
    try {
      if (!this.stickerManager) return;

      // 메인 스티커 뷰어의 회전 변경
      const mainViewer = this.stickerManager.getMainStickerViewer();
      if (mainViewer) {
        mainViewer.rotation.z = rotation;
      }
    } catch (error) {
      console.error('스티커 뷰어 회전 변경 중 오류 발생:', error);
    }
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Additional methods - 누락된 메서드들 (계속)
  // -----------------------------------------------------------------------------------------------------

  public oneSideAttachSticker(renderOrder: number, name?: string): any {
    try {
      if (!this.stickerManager || !this.raycastManager) return null;

      const intersection = this.raycastManager.getLastIntersection();
      if (!intersection) return null;

      const mainViewer = this.stickerManager.getMainStickerViewer();
      if (!mainViewer) return null;

      // 스티커 매니저를 통해 단면 스티커 부착
      const stickerInfo = this.stickerManager.attachOneSideSticker(
        'main', // 메인 뷰어 ID
        intersection.object as Mesh,
        intersection.point,
        renderOrder,
        name,
      );

      return stickerInfo;
    } catch (error) {
      console.error('단면 스티커 부착 중 오류 발생:', error);
      return null;
    }
  }

  public dualSidedStickerAttach(renderOrder: number, name?: string): any {
    try {
      if (!this.stickerManager || !this.raycastManager) return null;

      const intersection = this.raycastManager.getLastIntersection();
      if (!intersection) return null;

      const mainViewer = this.stickerManager.getMainStickerViewer();
      if (!mainViewer) return null;

      // 스티커 매니저를 통해 양면 스티커 부착
      const stickerInfo = this.stickerManager.attachDualSideSticker(
        'main', // 메인 뷰어 ID
        intersection.object as Mesh,
        intersection.point,
        renderOrder,
        name,
      );

      return stickerInfo;
    } catch (error) {
      console.error('양면 스티커 부착 중 오류 발생:', error);
      return null;
    }
  }

  public clearAllDecals(): void {
    try {
      if (!this.stickerManager) return;
      this.stickerManager.clearAllStickers();
    } catch (error) {
      console.error('모든 데칼 정리 중 오류 발생:', error);
    }
  }

  public changeStickerDepth(sticker: Mesh, depth: number): void {
    try {
      if (!this.stickerManager) return;

      // 스티커 ID를 찾아서 깊이 변경
      const stickers = this.stickerManager.getAllStickers();
      for (const [id, stickerInfo] of stickers) {
        if (stickerInfo.stickerMesh === sticker) {
          this.stickerManager.changeStickerDepth(id, depth);
          break;
        }
      }
    } catch (error) {
      console.error('스티커 깊이 변경 중 오류 발생:', error);
    }
  }

  public onDeleteStickerMode(index: number, stickerName: string): void {
    try {
      this.deleteStickerName = stickerName;
      this.deleteStickerIndex = index;
    } catch (error) {
      console.error('스티커 삭제 모드 활성화 중 오류 발생:', error);
    }
  }

  public offDeleteStickerMode(): void {
    try {
      this.deleteStickerName = '';
      this.deleteStickerIndex = -1;
    } catch (error) {
      console.error('스티커 삭제 모드 비활성화 중 오류 발생:', error);
    }
  }

  public screanShot(): string {
    try {
      if (!this.cameraManager) return '';
      return this.cameraManager.capturePhoto();
    } catch (error) {
      console.error('스크린샷 중 오류 발생:', error);
      return '';
    }
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Capture methods - 캡처 관련 메서드들
  // -----------------------------------------------------------------------------------------------------

  public async captureAllAngles(): Promise<{
    front: string;
    back: string;
    top: string;
    left: string;
  }> {
    try {
      if (!this.cameraManager) {
        throw new Error('카메라 매니저가 초기화되지 않았습니다.');
      }

      // CameraManager의 captureAllDirections 메서드 사용
      const results = await this.cameraManager.captureAllDirections(
        this.objectManager?.getMainObject() || new Group(),
      );

      return {
        front: results.front,
        back: results.back,
        top: results.top,
        left: results.left,
      };
    } catch (error) {
      console.error('전체 각도 캡처 중 오류 발생:', error);
      throw new Error('캡처에 실패했습니다: ' + (error as Error).message);
    }
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Private methods - Canvas Service 연결
  // -----------------------------------------------------------------------------------------------------

  /**
   * Canvas Service에 ThreeCanvasComponent를 전송하는 함수
   * 첫 렌더링이 완료된 후 호출됩니다.
   */
  private sendCanvasToService(): void {
    try {
      // Canvas Service에 컴포넌트 전송
      this.canvasService.initCavas(this);

      // 연결 상태 확인
      this.checkCanvasServiceConnection();
    } catch (error) {
      console.error('Canvas Service 연결 중 오류 발생:', error);
    }
  }

  /**
   * Canvas Service 연결 상태를 확인하는 함수
   */
  private checkCanvasServiceConnection(): void {
    try {
      const isConnected = this.canvasService.canvas === this;

      if (isConnected) {
        // 연결 성공 시 추가 초기화 작업
        this.onCanvasServiceConnected();
      }
    } catch (error) {
      console.error('Canvas Service 연결 상태 확인 중 오류 발생:', error);
    }
  }

  /**
   * Canvas Service 연결 성공 시 호출되는 함수
   */
  private onCanvasServiceConnected(): void {
    try {
      // 환경 설정
      if (this.environmentManager) {
        this.environmentHDR('./assets/images/hdr/potsdamer_platz_1k.hdr');
      }

      // 카메라 초기 설정
      if (this.cameraManager) {
        this.cameraManager.setCameraLocked(false);
      }
    } catch (error) {
      console.error('Canvas Service 연결 후 초기화 작업 중 오류 발생:', error);
    }
  }

  /**
   * 프로젝트 구독을 해제하는 함수
   */
  private unsubscribeFromProject(): void {
    if (this.projectSubscription) {
      this.projectSubscription.unsubscribe();
      this.projectSubscription = null;
    }
  }

  // -----------------------------------------------------------------------------------------------------
  // @ CanvasControllerService 연동 메서드들
  // -----------------------------------------------------------------------------------------------------

  /**
   * HDR 환경 설정 (CanvasControllerService에서 호출)
   */
  public environmentHDR(hdrPath: string): void {
    try {
      if (!this._scene || !this.renderManager) return;

      const renderer = this.renderManager.getRenderer();
      if (!renderer) return;

      // PMREMGenerator 생성
      const pmremGenerator = new THREE.PMREMGenerator(renderer);
      pmremGenerator.compileEquirectangularShader();

      // HDR 파일 로드 (RGBELoader 사용)
      import('three/examples/jsm/loaders/RGBELoader.js').then(({ RGBELoader }) => {
        const rgbeLoader = new RGBELoader();

        rgbeLoader.load(
          hdrPath,
          (texture) => {
            if (!this._scene) return;

            // 텍스처를 환경 맵으로 변환
            const envMap = pmremGenerator.fromEquirectangular(texture).texture;

            // Scene에 환경 맵 설정
            this._scene.environment = envMap;

            // 톤 매핑 설정으로 HDR 효과 최적화
            // renderer.toneMapping = THREE.ACESFilmicToneMapping;
            // renderer.toneMappingExposure = 0.78;
            // renderer.outputColorSpace = THREE.SRGBColorSpace;

            // 리소스 정리
            texture.dispose();
            pmremGenerator.dispose();
          },
          undefined,
          (error) => {
            console.error('HDR 환경 맵 로드 중 오류 발생:', error);
            this.setupFallbackEnvironment();
          },
        );
      });
    } catch (error) {
      console.error('HDR 환경 설정 중 오류 발생:', error);
    }
  }

  /**
   * 카메라 뷰 방향 설정 (CanvasControllerService에서 호출)
   */
  public lookAtControls(
    target: { x?: number; y?: number; z?: number },
    position: { x?: number; y?: number; z?: number },
    animate: boolean = true,
  ): void {
    try {
      if (!this.cameraManager) return;

      if (position) {
        this.cameraManager.setPosition(position.x || 0, position.y || 0, position.z || 0, animate);
      }

      if (target) {
        this.cameraManager.setTarget(target.x || 0, target.y || 0, target.z || 0, animate);
      }
    } catch (error) {
      console.error('카메라 뷰 설정 중 오류 발생:', error);
    }
  }

  /**
   * 첫 번째 카메라 설정 (CanvasControllerService에서 호출)
   */
  public firstCameraSet(): void {
    try {
      if (!this.cameraManager) return;

      // 기본 카메라 위치 및 타겟 설정
      this.cameraManager.setPosition(8, 5, 8, false);
      this.cameraManager.setTarget(0, 1, 0, false);
    } catch (error) {
      console.error('첫 번째 카메라 설정 중 오류 발생:', error);
    }
  }

  /**
   * 오브젝트 파일 추가 (CanvasControllerService에서 호출)
   */
  public async addObfuscatedFile(
    _d: any,
    _f: any,
    _o: any,
    _u: any,
    isBase: boolean = false,
  ): Promise<Object3D | undefined> {
    try {
      // 실제 구현에서는 파일 로딩 로직을 구현해야 함
      // 현재는 더미 오브젝트를 반환

      const { scene } = await this.loader.loadToObfuscatedFile(_d, _f);

      if (this.objectManager) {
        this.objectManager.addObject(scene);
      }

      if (this.cameraManager && isBase) {
        this.cameraManager.adjustCameraConstraintsForObject(scene);
      }

      this.isLoading.emit(false);
      return scene;
    } catch (error) {
      console.error('오브젝트 파일 추가 중 오류 발생:', error);
      this.isLoading.emit(false);
      return undefined;
    }
  }

  /**
   * 오브젝트 제거 (CanvasControllerService에서 호출)
   */
  public removeObject(object: Object3D): void {
    try {
      if (this._scene) {
        this._scene.remove(object);
      }

      // 오브젝트 정리
      this.disposeObject(object);
    } catch (error) {
      console.error('오브젝트 제거 중 오류 발생:', error);
    }
  }

  /**
   * 오브젝트 정리 (CanvasControllerService에서 호출)
   */
  private disposeObject(object: Object3D): void {
    try {
      object.traverse((child) => {
        if (child instanceof THREE.Mesh) {
          if (child.geometry) {
            child.geometry.dispose();
          }

          if (Array.isArray(child.material)) {
            child.material.forEach((mat) => mat.dispose());
          } else if (child.material) {
            child.material.dispose();
          }

          if (child.material && 'map' in child.material && child.material.map) {
            child.material.map.dispose();
          }
        }

        child.userData = {};
      });

      object.parent?.remove(object);
    } catch (error) {
      console.error('오브젝트 정리 중 오류 발생:', error);
    }
  }

  /**
   * 재질 색상 변경 (CanvasControllerService에서 호출)
   */
  public materialsColorChange(material: any, color: string): void {
    try {
      if (!material) return;

      if (Array.isArray(material)) {
        material.forEach((mat) => {
          if (mat.color) {
            mat.color.set(color);
            mat.needsUpdate = true;
          }
        });
      } else if (material.color) {
        material.color.set(color);
        material.needsUpdate = true;
      }
    } catch (error) {
      console.error('재질 색상 변경 중 오류 발생:', error);
    }
  }

  /**
   * 재질 이미지 변경 (CanvasControllerService에서 호출)
   */
  public materialsImageChange(materials: any[], imageUrl: string | undefined): void {
    try {
      if (!materials || materials.length === 0) return;

      if (!imageUrl) {
        materials.forEach((material) => {
          if (material) {
            material.map = material.userData['originalMaterial']?.map
              ? material.userData['originalMaterial'].map.clone()
              : null;
            material.color = material.userData['originalMaterial']?.color
              ? material.userData['originalMaterial'].color.clone()
              : null;
            material.needsUpdate = true;
          }
        });
        return;
      }

      const textureLoader = new THREE.TextureLoader();
      textureLoader.load(
        imageUrl,
        (texture) => {
          texture.colorSpace = THREE.SRGBColorSpace;
          texture.flipY = true;
          texture.mapping = THREE.EquirectangularReflectionMapping;
          texture.needsUpdate = true;
          materials.forEach((material) => {
            if (material) {
              material.map = texture;
              material.color = material.userData['originalMaterial']?.color
                ? new THREE.Color(0xffffff)
                : null;
              material.needsUpdate = true;
            }
          });
        },
        undefined,
        (error) => {
          console.error('재질 이미지 로드 중 오류 발생:', error);
        },
      );
    } catch (error) {
      console.error('재질 이미지 변경 중 오류 발생:', error);
    }
  }

  /**
   * 매트 재질 설정 (CanvasControllerService에서 호출)
   */
  public setMatteMaterial(material: any, isMatte: boolean): void {
    try {
      if (!material) return;

      if (Array.isArray(material)) {
        material.forEach((mat) => {
          if (mat && 'transparent' in mat) {
            mat.transparent = isMatte;
            mat.opacity = isMatte ? 0.5 : 1.0;
            mat.needsUpdate = true;
          }
        });
      } else if (material && 'transparent' in material) {
        material.transparent = isMatte;
        material.opacity = isMatte ? 0.5 : 1.0;
        material.needsUpdate = true;
      }
    } catch (error) {
      console.error('매트 재질 설정 중 오류 발생:', error);
    }
  }

  public selecteMeshAction(
    meshName: string,
    move?: { x: number; y: number; z: number },
    rotation?: { x: number; y: number; z: number },
    isAnimation?: boolean,
  ): void {
    if (!this._scene) return;
    this._scene.traverse((node: any) => {
      if (node instanceof THREE.Mesh && node.name.includes(meshName)) {
        if (!node.userData['original']) node.userData['original'] = {};
        node.userData['original'].rotation = node.rotation.clone();
        node.userData['original'].position = node.position.clone();

        if (rotation) {
          node.rotation.copy(node.userData['original'].rotation);
          node.position.copy(node.userData['original'].position);

          if (move) {
            const worldDirection = new Vector3(move.x, move.y, move.z);
            node.translateOnAxis(worldDirection, 1);
          }

          const box = new THREE.Box3().setFromObject(node);

          const center = new THREE.Vector3();
          box?.getCenter(center);

          const axisDirection = new THREE.Vector3(0, 0, 1).normalize();
          const angle = rotation.z;

          const quaternion = new THREE.Quaternion();
          quaternion.setFromAxisAngle(axisDirection, angle);
          node.applyQuaternion(quaternion);
          node.position.sub(center);
          node.position.applyQuaternion(quaternion); // 회전 적용
          node.position.add(center);

          node.updateMatrixWorld(true);
        } else if (move) {
          const worldDirection = new Vector3(move.x, move.y, move.z);
          node.translateOnAxis(worldDirection, 1);
        }
      }
    });
  }

  /**
   * 씬 클리어 (CanvasControllerService에서 호출)
   */
  public clear(): void {
    try {
      if (!this._scene) return;

      // 씬의 모든 오브젝트 제거
      while (this._scene.children.length > 0) {
        const child = this._scene.children[0];
        this._scene.remove(child);
      }
    } catch (error) {
      console.error('씬 클리어 중 오류 발생:', error);
    }
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Mesh 및 Material 분류 데이터 접근 메서드들
  // -----------------------------------------------------------------------------------------------------

  /**
   * 특정 카테고리의 메시들 조회
   */
  public getMeshesByCategory(category: string): THREE.Mesh[] {
    try {
      return this.meshCategories.get(category) || [];
    } catch (error) {
      console.error('카테고리별 메시 조회 중 오류 발생:', error);
      return [];
    }
  }

  /**
   * 모든 카테고리명 조회
   */
  public getAllCategories(): string[] {
    try {
      return Array.from(this.meshCategories.keys());
    } catch (error) {
      console.error('카테고리 목록 조회 중 오류 발생:', error);
      return [];
    }
  }

  /**
   * 활성 메테리얼 조회
   */
  public getActiveMaterials(): Map<string, THREE.Material> {
    try {
      return new Map(this.activeMaterials);
    } catch (error) {
      console.error('활성 메테리얼 조회 중 오류 발생:', error);
      return new Map();
    }
  }

  /**
   * 비활성 메테리얼 조회
   */
  public getInactiveMaterials(): Map<string, THREE.Material> {
    try {
      return new Map(this.inactiveMaterials);
    } catch (error) {
      console.error('비활성 메테리얼 조회 중 오류 발생:', error);
      return new Map();
    }
  }

  /**
   * 특정 카테고리의 메시 개수 조회
   */
  public getMeshCountByCategory(category: string): number {
    try {
      const meshes = this.meshCategories.get(category);
      return meshes ? meshes.length : 0;
    } catch (error) {
      console.error('카테고리별 메시 개수 조회 중 오류 발생:', error);
      return 0;
    }
  }

  /**
   * 분류 데이터 초기화
   */
  public clearClassificationData(): void {
    try {
      this.meshCategories.clear();
      this.activeMaterials.clear();
      this.inactiveMaterials.clear();
    } catch (error) {
      console.error('분류 데이터 초기화 중 오류 발생:', error);
    }
  }

  /**
   * 분류 데이터 상태 조회
   */
  public getClassificationStatus(): {
    totalCategories: number;
    totalMeshes: number;
    totalActiveMaterials: number;
    totalInactiveMaterials: number;
  } {
    try {
      let totalMeshes = 0;
      this.meshCategories.forEach((meshes) => {
        totalMeshes += meshes.length;
      });

      return {
        totalCategories: this.meshCategories.size,
        totalMeshes,
        totalActiveMaterials: this.activeMaterials.size,
        totalInactiveMaterials: this.inactiveMaterials.size,
      };
    } catch (error) {
      console.error('분류 데이터 상태 조회 중 오류 발생:', error);
      return {
        totalCategories: 0,
        totalMeshes: 0,
        totalActiveMaterials: 0,
        totalInactiveMaterials: 0,
      };
    }
  }
}
